import { io, Socket } from 'socket.io-client';
import { AppState, AppStateStatus } from 'react-native';
import { getToken, refreshAccessToken, api } from './api';

/**
 * Real-time socket singleton (ADDITIVE / progressive enhancement).
 *
 * This layers real-time delivery on top of the existing REST DM flow.
 * It is defensive by design: it NEVER throws if the socket is offline,
 * the token is missing, or the server is unreachable. The REST send/fetch
 * path keeps working exactly as before — this just makes it feel instant
 * when a connection is available.
 */

const SOCKET_URL = 'https://api.cryb.ai';
const DM_MESSAGE_EVENT = 'dm:message';
const DM_READ_EVENT = 'dm:read';
const DM_TYPING_EVENT = 'dm:typing';
const DM_REACTION_EVENT = 'dm:reaction';
// Emitted by the server (POST /voice/video/call) to the target user's room
// (`user:<id>`). See apps/api/src/routes/voice.ts → io.to(...).emit('incomingVideoCall', ...).
const INCOMING_CALL_EVENT = 'incomingVideoCall';

// --- SERVER CHANNEL events (A6) ---
// A new message in a server channel fires under TWO names (A13 dual-emit in the
// REST POST /messages path) — we listen for BOTH and normalize their differing
// payload shapes (camelCase wrapper vs. bare). The socket `message:create`
// handler (discord-realtime.ts) emits the bare form; the REST path emits both.
const CHANNEL_MSG_CREATE_CAMEL = 'messageCreate';      // { message, channelId }
const CHANNEL_MSG_CREATE_COLON = 'message:create';     // bare message
const CHANNEL_TYPING_START = 'channel:typing_start';   // { channel_id, user_id }
const CHANNEL_TYPING_STOP = 'channel:typing_stop';     // { channel_id, user_id }

// --- PERSONAL ROOM events (LR1) ---
// The server joins every socket to `user:<userId>` on connect (discord-realtime.ts)
// and emits per-user events there. Consumers (LR7 push, LR4 social, LR8 calling)
// subscribe via onUserEvent / onNotification without needing to know the room.
const NOTIFICATION_EVENT = 'notification';                 // a new Notification row
const NOTIFICATION_UNREAD_EVENT = 'notificationUnreadCount'; // { count } / { unreadCount }

export interface DmMessagePayload {
  conversationId: string;
  // Mirrors the REST message shape ({ id, content, userId, createdAt, User, ... }).
  message: any;
}

// Emitted to the SENDER when the OTHER participant marks the thread read.
export interface DmReadPayload {
  conversationId: string;
  readerId: string;
  lastReadMessageId: string;
}

// Emitted to the OTHER participant(s) while a user is actively typing.
export interface DmTypingPayload {
  conversationId: string;
  userId: string;
}

// Emitted to ALL participants when someone toggles a reaction on a DM message.
// `reactions` is the full, recomputed summary for the message (source of truth);
// `mine` in each entry is relative to the SERVER emit (recompute per-viewer).
export interface DmReactionPayload {
  conversationId: string;
  messageId: string;
  userId: string;
  emoji: string;
  action: 'added' | 'removed';
  reactions: Array<{ emoji: string; count: number; mine: boolean }>;
}

// Server payload for an inbound 1:1 video call (see voice.ts emit above).
// `initiator.username` is currently a placeholder server-side; the screen falls
// back gracefully when display info is thin.
export interface IncomingCallPayload {
  callId: string;
  roomName?: string;
  initiator?: { id?: string; username?: string; displayName?: string; avatar?: string };
  callType?: 'video' | 'audio';
  timestamp?: number;
}

// A new server-channel message, normalized to a single shape regardless of which
// of the two event names delivered it. `message` mirrors the REST message
// (PascalCase Prisma relations: User/MessageAttachment/…); `channelId` is the
// room the message belongs to (parsed from the wrapper or the bare message).
export interface ChannelMessagePayload {
  channelId: string;
  message: any;
}

// The other participant started/stopped typing in a server channel.
export interface ChannelTypingPayload {
  channelId: string;
  userId: string;
  typing: boolean;
}

type DmMessageHandler = (payload: DmMessagePayload) => void;
type DmReadHandler = (payload: DmReadPayload) => void;
type DmTypingHandler = (payload: DmTypingPayload) => void;
type DmReactionHandler = (payload: DmReactionPayload) => void;
type IncomingCallHandler = (payload: IncomingCallPayload) => void;
type ChannelMessageHandler = (payload: ChannelMessagePayload) => void;
type ChannelTypingHandler = (payload: ChannelTypingPayload) => void;

let socket: Socket | null = null;
let connecting = false;
let appStateSub: { remove: () => void } | null = null;

// Subscribers are tracked locally so a reconnect (new Socket instance) keeps
// delivering to the same callbacks without callers having to re-subscribe.
const dmHandlers = new Set<DmMessageHandler>();
const dmReadHandlers = new Set<DmReadHandler>();
const dmTypingHandlers = new Set<DmTypingHandler>();
const dmReactionHandlers = new Set<DmReactionHandler>();
const incomingCallHandlers = new Set<IncomingCallHandler>();
const channelMsgHandlers = new Set<ChannelMessageHandler>();
const channelTypingHandlers = new Set<ChannelTypingHandler>();

// Personal-room subscribers (LR1). `notificationHandlers` / `unreadHandlers` are
// the typed common cases; `userEventHandlers` is a generic name→callbacks map so a
// consumer can listen for ANY `user:<id>` event (badgeAwarded, userUpdate, …)
// without this file needing to know about every event name up front.
type NotificationHandler = (payload: any) => void;
type UnreadHandler = (count: number) => void;
type GenericHandler = (payload: any) => void;
const notificationHandlers = new Set<NotificationHandler>();
const unreadHandlers = new Set<UnreadHandler>();
const userEventHandlers = new Map<string, Set<GenericHandler>>();
// Event names we've wired a passthrough listener for on the current socket, so we
// don't double-bind the same generic event across reconnects.
const boundGenericEvents = new Set<string>();

// Fires after a reconnect that did NOT restore session state (recovered===false),
// so feature layers (LR2/LR3/LR5/LR7) know they must REST-resync missed data.
type ReconnectHandler = (info: { recovered: boolean }) => void;
const reconnectHandlers = new Set<ReconnectHandler>();

// Channel rooms we've asked the server to join, so a reconnect (which drops all
// server-side room membership) can transparently re-join them. Without this,
// after a backgrounded socket reconnects, channel messages stop arriving.
const joinedChannels = new Set<string>();

// Server + community rooms we've asked to join (LR5), tracked exactly like
// joinedChannels so a reconnect transparently re-joins them and live member-count
// events (`server:member_update` / `community:member_update`) keep arriving after
// a background→foreground reconnect. Server-side room membership is per-connection.
const joinedServers = new Set<string>();
const joinedCommunities = new Set<string>();

// Dedupe key for a just-delivered channel message: BOTH event names fire for the
// same REST send, so without this every channel message would arrive twice. We
// remember the last few delivered message ids and drop a second delivery.
const recentChannelMsgIds: string[] = [];
function channelMsgAlreadyDelivered(id: string): boolean {
  if (!id) return false;
  if (recentChannelMsgIds.includes(id)) return true;
  recentChannelMsgIds.push(id);
  if (recentChannelMsgIds.length > 80) recentChannelMsgIds.shift();
  return false;
}

function attachDmListener(s: Socket) {
  s.on(DM_MESSAGE_EVENT, (payload: DmMessagePayload) => {
    dmHandlers.forEach((cb) => {
      try {
        cb(payload);
      } catch {
        // A bad handler must never break socket delivery.
      }
    });
  });

  // Read receipts: the OTHER participant marked the thread read.
  s.on(DM_READ_EVENT, (payload: DmReadPayload) => {
    dmReadHandlers.forEach((cb) => {
      try {
        cb(payload);
      } catch {
        // A bad handler must never break socket delivery.
      }
    });
  });

  // Typing indicators: the OTHER participant is actively typing.
  s.on(DM_TYPING_EVENT, (payload: DmTypingPayload) => {
    dmTypingHandlers.forEach((cb) => {
      try {
        cb(payload);
      } catch {
        // A bad handler must never break socket delivery.
      }
    });
  });

  // Reactions: any participant toggled a reaction on a message in the thread.
  s.on(DM_REACTION_EVENT, (payload: DmReactionPayload) => {
    dmReactionHandlers.forEach((cb) => {
      try {
        cb(payload);
      } catch {
        // A bad handler must never break socket delivery.
      }
    });
  });

  // Inbound 1:1 video call: someone is calling THIS user.
  s.on(INCOMING_CALL_EVENT, (payload: IncomingCallPayload) => {
    incomingCallHandlers.forEach((cb) => {
      try {
        cb(payload);
      } catch {
        // A bad handler must never break socket delivery.
      }
    });
  });

  // --- SERVER CHANNEL new message (TWO event names, one delivery) ---
  // A13's REST POST /messages emits BOTH `messageCreate` ({message,channelId})
  // and `message:create` (bare). We attach to both and fan out a single
  // normalized ChannelMessagePayload, deduped by message id so the double-emit
  // never produces a duplicate bubble at the transport layer.
  const fanOutChannelMessage = (channelId: string, message: any) => {
    const id = message?.id;
    if (channelMsgAlreadyDelivered(String(id ?? ''))) return;
    const payload: ChannelMessagePayload = { channelId: String(channelId ?? ''), message };
    channelMsgHandlers.forEach((cb) => {
      try {
        cb(payload);
      } catch {
        // A bad handler must never break socket delivery.
      }
    });
  };
  s.on(CHANNEL_MSG_CREATE_CAMEL, (payload: { message?: any; channelId?: string }) => {
    if (!payload?.message) return;
    const channelId = payload.channelId ?? payload.message?.channelId ?? '';
    fanOutChannelMessage(channelId, payload.message);
  });
  s.on(CHANNEL_MSG_CREATE_COLON, (message: any) => {
    if (!message) return;
    fanOutChannelMessage(message?.channelId ?? '', message);
  });

  // --- SERVER CHANNEL typing (start/stop carry {channel_id,user_id}) ---
  const fanOutChannelTyping = (channelId: string, userId: string, typing: boolean) => {
    const payload: ChannelTypingPayload = { channelId: String(channelId ?? ''), userId: String(userId ?? ''), typing };
    channelTypingHandlers.forEach((cb) => {
      try {
        cb(payload);
      } catch {
        // ignore
      }
    });
  };
  s.on(CHANNEL_TYPING_START, (p: { channel_id?: string; user_id?: string }) => {
    fanOutChannelTyping(p?.channel_id ?? '', p?.user_id ?? '', true);
  });
  s.on(CHANNEL_TYPING_STOP, (p: { channel_id?: string; user_id?: string }) => {
    fanOutChannelTyping(p?.channel_id ?? '', p?.user_id ?? '', false);
  });

  // --- PERSONAL ROOM: notifications + unread count (LR1) ---
  s.on(NOTIFICATION_EVENT, (payload: any) => {
    notificationHandlers.forEach((cb) => {
      try {
        cb(payload);
      } catch {
        // ignore
      }
    });
  });
  s.on(NOTIFICATION_UNREAD_EVENT, (payload: any) => {
    const count = Number(payload?.count ?? payload?.unreadCount ?? payload);
    if (!Number.isFinite(count)) return;
    unreadHandlers.forEach((cb) => {
      try {
        cb(count);
      } catch {
        // ignore
      }
    });
  });

  // Re-bind any generic user-event passthroughs that callers registered before
  // this (re)connect created a fresh socket instance.
  boundGenericEvents.clear();
  userEventHandlers.forEach((_set, eventName) => bindGenericEvent(s, eventName));

  // --- Manager-level reconnect (LR1) ---
  // Listen on the MANAGER (socket.io), not the socket, for reconnection. On a
  // reconnect, Socket.IO sets socket.recovered=true ONLY if connectionStateRecovery
  // restored the session+missed events. If recovered is false, downstream feature
  // layers must REST-resync — we fan that out via reconnectHandlers.
  try {
    s.io.on('reconnect', () => {
      const recovered = !!(s as any).recovered;
      reconnectHandlers.forEach((cb) => {
        try {
          cb({ recovered });
        } catch {
          // ignore
        }
      });
    });
  } catch {
    // ignore — manager may not be available in odd transport states
  }

  // 'io server disconnect' means the SERVER deliberately closed us (e.g. a deploy
  // or an auth kick); Socket.IO will NOT auto-reconnect in that case, so we must
  // reconnect manually or live delivery silently dies until the next app focus.
  s.on('disconnect', (reason: string) => {
    if (reason === 'io server disconnect') {
      try {
        s.connect();
      } catch {
        // ignore
      }
    }
  });

  // On (re)connect, re-join every channel room we were subscribed to so live
  // delivery survives a background→foreground reconnect (server-side room
  // membership is per-connection and is lost on a new connection). The personal
  // `user:<id>` room is re-joined SERVER-side on connect (discord-realtime.ts), so
  // notifications/DM events resume automatically — only channel rooms need replay.
  s.on('connect', () => {
    joinedChannels.forEach((cid) => {
      try {
        s.emit('channel:join', { channelId: cid });
      } catch {
        // ignore
      }
    });
    // Re-join server + community rooms (LR5) so live member-count updates survive
    // a reconnect. Best-effort per room; a failed emit just misses that update.
    joinedServers.forEach((sid) => {
      try { s.emit('server:join', { serverId: sid }); } catch { /* ignore */ }
    });
    joinedCommunities.forEach((cid) => {
      try { s.emit('community:join', { communityId: cid }); } catch { /* ignore */ }
    });
  });
}

/** Bind a single generic user-event passthrough on a socket instance (once). */
function bindGenericEvent(s: Socket, eventName: string): void {
  if (boundGenericEvents.has(eventName)) return;
  boundGenericEvents.add(eventName);
  s.on(eventName, (payload: any) => {
    const set = userEventHandlers.get(eventName);
    if (!set) return;
    set.forEach((cb) => {
      try {
        cb(payload);
      } catch {
        // ignore
      }
    });
  });
}

/**
 * Join a server channel's realtime room. Idempotent + reconnect-safe: the room
 * is remembered and automatically re-joined on every (re)connect. Safe to call
 * before the socket has finished connecting (the `connect` handler replays it).
 */
export async function joinChannel(channelId: string): Promise<void> {
  if (!channelId) return;
  joinedChannels.add(channelId);
  const s = await connectSocket();
  try {
    if (s && s.connected) s.emit('channel:join', { channelId });
  } catch {
    // ignore — the connect handler will re-join once connected
  }
}

/** Leave a server channel's realtime room and stop auto-rejoining it. */
export function leaveChannel(channelId: string): void {
  if (!channelId) return;
  joinedChannels.delete(channelId);
  try {
    if (socket && socket.connected) socket.emit('channel:leave', { channelId });
  } catch {
    // ignore
  }
}

/**
 * Emit a typing ping for a server channel (server re-broadcasts as
 * `channel:typing_start` to other members and auto-stops after ~10s).
 * Fire-and-forget; never throws.
 */
export function emitChannelTyping(channelId: string): void {
  if (!channelId) return;
  try {
    if (socket && socket.connected) socket.emit('channel:typing', { channelId });
  } catch {
    // ignore
  }
}

/**
 * Join a SERVER's realtime room (LR5). Idempotent + reconnect-safe: the room is
 * remembered and re-joined on every (re)connect. Subscribe to the live member
 * count via `onUserEvent('server:member_update', cb)` after calling this.
 */
export async function joinServerRoom(serverId: string): Promise<void> {
  if (!serverId) return;
  joinedServers.add(serverId);
  const s = await connectSocket();
  try {
    if (s && s.connected) s.emit('server:join', { serverId });
  } catch {
    // ignore — the connect handler re-joins once connected
  }
}

/** Leave a server's realtime room and stop auto-rejoining it (LR5). */
export function leaveServerRoom(serverId: string): void {
  if (!serverId) return;
  joinedServers.delete(serverId);
  try {
    if (socket && socket.connected) socket.emit('server:leave', { serverId });
  } catch {
    // ignore
  }
}

/**
 * Join a COMMUNITY's realtime room (LR5). Idempotent + reconnect-safe. The
 * server emits `community:member_update` to `community:<id>` AND `community:<name>`,
 * so pass whichever id you have; subscribe via onUserEvent('community:member_update').
 */
export async function joinCommunityRoom(communityId: string): Promise<void> {
  if (!communityId) return;
  joinedCommunities.add(communityId);
  const s = await connectSocket();
  try {
    if (s && s.connected) s.emit('community:join', { communityId });
  } catch {
    // ignore — the connect handler re-joins once connected
  }
}

/** Leave a community's realtime room and stop auto-rejoining it (LR5). */
export function leaveCommunityRoom(communityId: string): void {
  if (!communityId) return;
  joinedCommunities.delete(communityId);
  try {
    if (socket && socket.connected) socket.emit('community:leave', { communityId });
  } catch {
    // ignore
  }
}

/** Subscribe to new server-channel messages. Returns an unsubscribe function. */
export function onChannelMessage(cb: ChannelMessageHandler): () => void {
  channelMsgHandlers.add(cb);
  connectSocket();
  return () => {
    channelMsgHandlers.delete(cb);
  };
}

/** Subscribe to server-channel typing start/stop. Returns an unsubscribe fn. */
export function onChannelTyping(cb: ChannelTypingHandler): () => void {
  channelTypingHandlers.add(cb);
  connectSocket();
  return () => {
    channelTypingHandlers.delete(cb);
  };
}

/**
 * Subscribe to in-app notifications delivered to this user's personal room
 * (`user:<id>`). The server emits `notification` for every new Notification row.
 * Consumed by LR7 (push/notifications). Returns an unsubscribe function.
 */
export function onNotification(cb: (payload: any) => void): () => void {
  notificationHandlers.add(cb);
  connectSocket();
  return () => {
    notificationHandlers.delete(cb);
  };
}

/**
 * Subscribe to live unread-notification-count updates (`notificationUnreadCount`).
 * Fires with the new total so the nav badge can update without a refetch.
 */
export function onNotificationUnreadCount(cb: (count: number) => void): () => void {
  unreadHandlers.add(cb);
  connectSocket();
  return () => {
    unreadHandlers.delete(cb);
  };
}

/**
 * Subscribe to ANY event delivered to this user's personal room by name
 * (e.g. 'badgeAwarded', 'userUpdate', 'userAvatarUpdate', 'incomingVideoCall',
 * 'message:mention'). Generic escape hatch for feature layers (LR4/LR7/LR8) so
 * they don't each have to add a typed wrapper here. Returns an unsubscribe fn.
 */
export function onUserEvent(eventName: string, cb: (payload: any) => void): () => void {
  if (!eventName) return () => {};
  let set = userEventHandlers.get(eventName);
  if (!set) {
    set = new Set();
    userEventHandlers.set(eventName, set);
  }
  set.add(cb);
  // Bind the passthrough on the live socket if one already exists; otherwise the
  // attach step on (re)connect will bind it.
  if (socket) bindGenericEvent(socket, eventName);
  connectSocket();
  return () => {
    const s = userEventHandlers.get(eventName);
    if (s) s.delete(cb);
  };
}

/**
 * Subscribe to reconnect events. The callback receives `{ recovered }`:
 *  - recovered === true  → Socket.IO restored the session + replayed missed events;
 *    NO REST resync needed.
 *  - recovered === false → the disconnect exceeded the recovery window; the caller
 *    SHOULD REST-resync missed data (see resyncSince / fetchUnreadCounts).
 * This is the hook LR2/LR3/LR5/LR7 use to know when to pull missed data.
 */
export function onReconnect(cb: (info: { recovered: boolean }) => void): () => void {
  reconnectHandlers.add(cb);
  connectSocket();
  return () => {
    reconnectHandlers.delete(cb);
  };
}

/**
 * Emit an event with an ack + timeout-retry (LR1 at-least-once for emits that
 * matter). Resolves with the server ack payload, or null if all attempts time out.
 * The caller MUST include a stable id in `payload` (e.g. clientMessageId) so the
 * server insert is idempotent and a retry can't double-create. Never throws.
 *
 *   const ack = await emitWithAck('message:create', { ...data, nonce });
 */
export async function emitWithAck<T = any>(
  event: string,
  payload: any,
  opts: { timeoutMs?: number; retries?: number } = {}
): Promise<T | null> {
  const timeoutMs = opts.timeoutMs ?? 4000;
  const retries = opts.retries ?? 2;
  const s = await connectSocket();
  if (!s) return null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const result = await new Promise<{ ok: boolean; data?: T }>((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        resolve({ ok: false });
      }, timeoutMs);
      try {
        s.timeout(timeoutMs).emit(event, payload, (err: any, ack: T) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          if (err) resolve({ ok: false });
          else resolve({ ok: true, data: ack });
        });
      } catch {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve({ ok: false });
        }
      }
    });
    if (result.ok) return result.data ?? (null as any);
    // brief backoff before retrying
    await new Promise<void>((r) => setTimeout(() => r(), 300 * (attempt + 1)));
  }
  return null;
}

/**
 * Ensure the socket is connected. Safe to call repeatedly. Resolves to the
 * Socket instance, or null if we couldn't connect (e.g. no token / offline).
 */
export async function connectSocket(): Promise<Socket | null> {
  try {
    if (socket && socket.connected) return socket;
    if (connecting) return socket;

    const token = await getToken();
    if (!token) return null;

    connecting = true;

    // Reuse the existing instance if present (e.g. reconnecting after focus),
    // otherwise create a fresh one wired with the current token.
    if (socket) {
      // Refresh auth in case the token rotated, then reconnect.
      (socket.auth as any) = { token };
      if (!socket.connected) socket.connect();
      connecting = false;
      return socket;
    }

    socket = io(SOCKET_URL, {
      path: '/socket.io/',
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
      autoConnect: true,
    });

    attachDmListener(socket);

    // Best-effort: refresh the token on every (re)connection attempt so a
    // rotated JWT doesn't permanently lock us out.
    socket.io.on('reconnect_attempt', async () => {
      try {
        const fresh = await getToken();
        if (fresh && socket) (socket.auth as any) = { token: fresh };
      } catch {
        // ignore
      }
    });

    // If the handshake is rejected because the access token expired (the user
    // sat idle with no REST activity to refresh it), mint a fresh one and
    // reconnect. Without this the socket re-reads the SAME stale token on every
    // reconnect_attempt and live delivery never recovers until an API call runs.
    socket.on('connect_error', async () => {
      try {
        const fresh = await refreshAccessToken();
        if (fresh && socket) {
          (socket.auth as any) = { token: fresh };
          socket.connect();
        }
      } catch {
        // ignore — REST remains the source of truth, so DMs still work.
      }
    });

    // Set up app-focus driven reconnect exactly once.
    if (!appStateSub) {
      appStateSub = AppState.addEventListener('change', (state: AppStateStatus) => {
        if (state === 'active') {
          // Returning to foreground — make sure we're connected.
          connectSocket();
        }
      });
    }

    connecting = false;
    return socket;
  } catch {
    connecting = false;
    return null;
  }
}

/** Disconnect and tear down the singleton. Never throws. */
export function disconnectSocket(): void {
  try {
    if (appStateSub) {
      appStateSub.remove();
      appStateSub = null;
    }
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
    dmHandlers.clear();
    dmReadHandlers.clear();
    dmTypingHandlers.clear();
    incomingCallHandlers.clear();
    channelMsgHandlers.clear();
    channelTypingHandlers.clear();
    notificationHandlers.clear();
    unreadHandlers.clear();
    userEventHandlers.clear();
    boundGenericEvents.clear();
    reconnectHandlers.clear();
    joinedChannels.clear();
    joinedServers.clear();
    joinedCommunities.clear();
    recentChannelMsgIds.length = 0;
  } catch {
    // ignore
  }
}

/** Subscribe to incoming DM messages. Returns an unsubscribe function. */
export function onDmMessage(cb: DmMessageHandler): () => void {
  dmHandlers.add(cb);
  // Lazily ensure a connection exists when someone starts listening.
  connectSocket();
  return () => offDmMessage(cb);
}

/** Unsubscribe a previously-registered DM message handler. */
export function offDmMessage(cb: DmMessageHandler): void {
  dmHandlers.delete(cb);
}

/**
 * Subscribe to DM read receipts. Fires when the OTHER participant marks the
 * thread read (so the sender can show "Read"). Returns an unsubscribe function.
 */
export function onDmRead(cb: DmReadHandler): () => void {
  dmReadHandlers.add(cb);
  // Lazily ensure a connection exists when someone starts listening.
  connectSocket();
  return () => {
    dmReadHandlers.delete(cb);
  };
}

/**
 * Subscribe to DM typing indicators. Fires when the OTHER participant is
 * actively typing. Returns an unsubscribe function.
 */
export function onDmTyping(cb: DmTypingHandler): () => void {
  dmTypingHandlers.add(cb);
  // Lazily ensure a connection exists when someone starts listening.
  connectSocket();
  return () => {
    dmTypingHandlers.delete(cb);
  };
}

/**
 * Subscribe to DM reaction updates. Fires when any participant (including this
 * user's other devices) toggles a reaction. Returns an unsubscribe function.
 */
export function onDmReaction(cb: DmReactionHandler): () => void {
  dmReactionHandlers.add(cb);
  // Lazily ensure a connection exists when someone starts listening.
  connectSocket();
  return () => {
    dmReactionHandlers.delete(cb);
  };
}

/**
 * Subscribe to inbound 1:1 video calls (the `incomingVideoCall` server event).
 * Fires when another user starts a direct call to THIS user. Returns an
 * unsubscribe function. Mirrors onDmMessage/onDmRead/onDmTyping.
 */
export function onIncomingCall(cb: IncomingCallHandler): () => void {
  incomingCallHandlers.add(cb);
  // Lazily ensure a connection exists when someone starts listening.
  connectSocket();
  return () => {
    incomingCallHandlers.delete(cb);
  };
}

/** Whether the socket is currently connected (for optional UI hints). */
export function isSocketConnected(): boolean {
  return !!socket && socket.connected;
}

/**
 * Whether the LAST (re)connection restored session state via Socket.IO's
 * connectionStateRecovery. When false after a reconnect, callers should REST-resync.
 */
export function wasRecovered(): boolean {
  return !!(socket && (socket as any).recovered);
}

// --- REST RESYNC ("since last id") — the reconnect safety net (LR1) ----------
// When a reconnect is NOT recovered (gap > 2 min, cold start, long background),
// these pull everything missed in one call. Shapes match apps/api routes/realtime-sync.ts.

export interface ResyncResult {
  serverTime: string;
  notifications: any[];
  unreadNotifications: number;
  dmMessages: any[]; // each carries `conversationId`
  dmUnreadByConversation: Record<string, number>;
  channelMessages: Record<string, any[]>;
}

/**
 * Pull everything missed since `sinceISO` (ISO8601). Pass the channel ids the
 * client currently has open so their missed messages come back too. Returns null
 * on failure (caller keeps its current state — REST history remains the fallback).
 * Use `result.serverTime` as the next `since` cursor.
 */
export async function resyncSince(
  sinceISO: string,
  channelIds: string[] = []
): Promise<ResyncResult | null> {
  try {
    const params = new URLSearchParams();
    if (sinceISO) params.set('since', sinceISO);
    if (channelIds.length > 0) params.set('channelIds', channelIds.join(','));
    const res = await api<ResyncResult>(`/sync/since?${params.toString()}`);
    if (res.success && res.data) {
      const d = res.data as any;
      return {
        serverTime: d.serverTime ?? new Date().toISOString(),
        notifications: Array.isArray(d.notifications) ? d.notifications : [],
        unreadNotifications: Number(d.unreadNotifications) || 0,
        dmMessages: Array.isArray(d.dmMessages) ? d.dmMessages : [],
        dmUnreadByConversation: d.dmUnreadByConversation && typeof d.dmUnreadByConversation === 'object' ? d.dmUnreadByConversation : {},
        channelMessages: d.channelMessages && typeof d.channelMessages === 'object' ? d.channelMessages : {},
      };
    }
    return null;
  } catch {
    return null;
  }
}

export interface UnreadCounts {
  notifications: number;
  dmTotal: number;
  dmByConversation: Record<string, number>;
}

/**
 * Lightweight badge reconciliation after a reconnect: just the unread counts
 * (notifications + DM total + per-conversation), no message bodies. Returns null
 * on failure. Cheaper than resyncSince when only nav dots need fixing.
 */
export async function fetchUnreadCounts(): Promise<UnreadCounts | null> {
  try {
    const res = await api<UnreadCounts>('/sync/unread-counts');
    if (res.success && res.data) {
      const d = res.data as any;
      return {
        notifications: Number(d.notifications) || 0,
        dmTotal: Number(d.dmTotal) || 0,
        dmByConversation: d.dmByConversation && typeof d.dmByConversation === 'object' ? d.dmByConversation : {},
      };
    }
    return null;
  } catch {
    return null;
  }
}
